import { queryOptions, useQuery } from "@tanstack/react-query";
import { apiFetch } from "./api";

export interface CompanyRow {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

export interface DepartmentRow {
  id: string;
  companyId: string;
  parentId: string | null;
  code: string;
  name: string;
  isCostCenter: boolean;
  managerUserId: string | null;
  sortOrder: number;
  isActive: boolean;
}

export interface LocationRow {
  id: string;
  code: string;
  name: string;
  companyId: string | null;
  address: string | null;
  building: string | null;
  floor: string | null;
  room: string | null;
  isActive: boolean;
}

export interface WarehouseRow {
  id: string;
  code: string;
  name: string;
  companyId: string | null;
  warehouseType: string;
  locationId: string | null;
  managerUserId: string | null;
  isActive: boolean;
}

export interface PersonnelRow {
  id: string;
  employeeNo: string | null;
  name: string;
  companyId: string | null;
  departmentId: string | null;
  costCenterId: string | null;
  userId: string | null;
  managerId: string | null;
  position: string | null;
  phone: string | null;
  email: string | null;
  adid: string | null;
  isActive: boolean;
}

export interface MaterialLevelRow {
  id: string;
  code: number;
  name: string;
  sortOrder: number;
  isActive: boolean;
}

export interface MaterialCategoryRow {
  id: string;
  levelId: string;
  code: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
}

export interface MaterialProductRow {
  id: string;
  categoryId: string;
  code: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
}

export interface MaterialSpecRow {
  id: string;
  productId: string;
  code: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
}

export interface MaterialRow {
  id: string;
  code: string;
  name: string;
  levelId: string;
  categoryId: string | null;
  productId: string | null;
  specConfigId: string | null;
  category: string;
  unit: string;
  estimatedUnitPriceCents: number | null;
  expectedLifeDays: number;
  companyId: string | null;
  status: string;
  classificationOverrideReason: string | null;
}

export interface ReferenceData {
  companies: CompanyRow[];
  departments: DepartmentRow[];
  locations: LocationRow[];
  warehouses: WarehouseRow[];
  users: { id: string; displayName: string; status: string }[];
  materialLevels: MaterialLevelRow[];
  materialCategories: MaterialCategoryRow[];
  materialProducts: MaterialProductRow[];
  materialSpecConfigs: MaterialSpecRow[];
  basicDataOptions: {
    id: string;
    dataType: string;
    category: string | null;
    code: string;
    name: string;
    sortOrder: number;
    isActive: boolean;
  }[];
}

export const referenceQueryOptions = queryOptions({
  queryKey: ["reference-data"] as const,
  queryFn: () => apiFetch<ReferenceData>("/api/v1/reference-data"),
  staleTime: 60_000,
});

export function useReferenceData() {
  return useQuery(referenceQueryOptions);
}

export function listResource<T>(path: string): Promise<T[]> {
  return apiFetch<T[]>(path);
}

export function createResource<T>(path: string, body: unknown): Promise<T> {
  return apiFetch<T>(path, { method: "POST", body: JSON.stringify(body) });
}

export function updateResource<T>(path: string, body: unknown): Promise<T> {
  return apiFetch<T>(path, { method: "PATCH", body: JSON.stringify(body) });
}

export function putResource<T>(path: string, body: unknown): Promise<T> {
  return apiFetch<T>(path, { method: "PUT", body: JSON.stringify(body) });
}

export function materialCategoryLabel(category: string): string {
  return (
    { asset: "资产", accessory: "低值易耗", consumable: "耗材", software: "软件" }[category] ??
    category
  );
}

export function warehouseTypeLabel(type: string): string {
  return { entity: "实体仓", virtual: "虚拟仓", repair: "维修仓", other: "其他" }[type] ?? type;
}

export interface AssetRow {
  id: string;
  assetCode: string;
  materialId: string;
  serialNumber: string | null;
  financialCardNo: string | null;
  status: string;
  condition: string;
  purchaseUnitPriceCents: number;
  inboundDate: string | null;
  warrantyExpiry: string | null;
  costCenterId: string | null;
  locationId: string | null;
  locationText: string | null;
  assetManagerId: string | null;
  holderId: string | null;
  borrowDueAt: string | null;
  hostname: string | null;
  modelSpec: string | null;
  brandOptionId: string | null;
}

export interface AccessoryRow {
  id: string;
  code: string;
  materialId: string;
  status: string;
  purchaseUnitPriceCents: number;
  inboundDate: string | null;
  financialCardNo: string | null;
  compatibleModel: string | null;
  location: string | null;
  holderId: string | null;
  relatedAssetId: string | null;
}

export interface StockRow {
  materialId: string;
  quantity: number;
  updatedAt: string;
  materialCode: string;
  materialName: string;
  unit: string;
  companyId: string | null;
}

export interface LicenseRow {
  id: string;
  licenseCode: string;
  materialId: string;
  mode: string;
  startDate: string;
  expiryDate: string | null;
  purchaseUnitPriceCents: number;
  totalSeats: number;
  allocatedSeats: number;
  vendor: string | null;
  status: string;
  seats: {
    id: string;
    seatCode: string;
    status: string;
    userId: string | null;
    assetId: string | null;
  }[];
}

export interface LabelTaskRow {
  id: string;
  taskNo: string;
  targetType: "asset" | "accessory";
  targetId: string;
  materialName: string;
  deviceCode: string;
  financialCardNo: string | null;
  productName: string;
  usageLocation: string;
  quantity: number;
  status: string;
  source: string;
  printedAt: string | null;
  payloadJson: string | null;
  createdAt: string;
}

export interface RequestStepRow {
  id: string;
  stepNo: number;
  approverRole: string;
  approverUserId: string;
  status: string;
  comment: string | null;
  actedAt: string | null;
  dueAt: string | null;
}

export interface HandoverRow {
  id: string;
  requestId: string;
  assetId: string | null;
  accessoryId: string | null;
  licenseId: string | null;
  seatId: string | null;
  recipientId: string;
  status: string;
  dueAt: string;
  confirmedAt: string | null;
  rejectedReason: string | null;
  confirmationText: string | null;
}

export interface RequestRow {
  id: string;
  requestNo: string;
  requestType: string;
  requesterId: string;
  materialId: string;
  quantity: number;
  amountCents: number;
  purpose: string | null;
  neededBy: string | null;
  borrowDays: number | null;
  status: string;
  currentStep: number;
  createdAt: string;
  material: { id: string; name: string; code: string } | null;
  requester: { id: string; displayName: string } | null;
  steps: RequestStepRow[];
  handovers: HandoverRow[];
}

export interface AuditRow {
  id: string;
  seq: number;
  occurredAt: string;
  actorUserId: string | null;
  action: string;
  category: string;
  objectType: string | null;
  objectId: string | null;
  objectLabel: string | null;
  before: unknown;
  after: unknown;
  diff: unknown;
  result: string;
  hash: string;
  requestId: string | null;
}

export interface ReminderRow {
  id: string;
  type: "handover" | "return" | "license" | "borrow" | "stock";
  severity: "overdue" | "due_soon" | "warning";
  title: string;
  detail: string;
  dueAt: string | null;
  targetId: string;
}

export interface ReportSummary {
  generatedAt: string;
  assets: {
    total: number;
    totalValueCents: number;
    byStatus: { status: string; count: number; valueCents: number }[];
    byCategory: { category: string; count: number; valueCents: number }[];
  };
  requests: {
    total: number;
    byStatus: { status: string; count: number }[];
    byType: { requestType: string; count: number }[];
  };
  accessories: { total: number; byStatus: { status: string; count: number }[] };
  licenses: {
    total: number;
    active: number;
    expiring90Days: number;
    expired: number;
    totalSeats: number;
    allocatedSeats: number;
  };
  consumables: { kinds: number; quantity: number; lowStock: number; outOfStock: number };
}

export interface AttachmentRow {
  id: string;
  objectKey: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  sha256: string;
  targetType: string;
  targetId: string;
  category: string | null;
  uploadedBy: string | null;
  createdAt: string;
}

export interface MaintenancePartRow {
  id: string;
  partName: string;
  quantity: number;
  unitCostCents: number;
  materialId: string | null;
}

export interface MaintenanceRow {
  id: string;
  ticketNo: string;
  assetId: string;
  reporterId: string;
  description: string;
  status: string;
  costCents: number;
  costApprovalRequired: boolean;
  vendor: string | null;
  sentAt: string | null;
  expectedAt: string | null;
  completedAt: string | null;
  result: string | null;
  createdAt: string;
  asset: { id: string; assetCode: string; materialId: string } | null;
  material: { id: string; name: string; code: string } | null;
  reporter: { id: string; displayName: string } | null;
  costApproval: {
    id: string;
    approverId: string;
    status: string;
    comment: string | null;
    actedAt: string | null;
    dueAt: string | null;
  } | null;
  parts: MaintenancePartRow[];
}

export interface AssetTransitionRow {
  id: string;
  assetId: string;
  fromStatus: string;
  toStatus: string;
  action: string;
  actorId: string | null;
  notes: string | null;
  occurredAt: string;
}

export interface AssetDetail {
  asset: AssetRow & {
    borrowDueAt: string | null;
    purchaseOrderNo: string | null;
    contractNo: string | null;
    inboundDate: string | null;
    warrantyExpiry: string | null;
    attributesJson: string | null;
    minorIssuesJson: string | null;
    createdAt: string;
    updatedAt: string;
  };
  transitions: AssetTransitionRow[];
  returnConfirmation: {
    id: string;
    holderId: string;
    status: string;
    dueAt: string;
    previousStatus: string;
  } | null;
}

export interface StockMovementRow {
  id: string;
  materialId: string;
  batchId: string | null;
  movementType: string;
  quantityDelta: number;
  referenceType: string | null;
  referenceId: string | null;
  reason: string | null;
  actorId: string | null;
  occurredAt: string;
}

export interface UserDataScopeRow {
  id: string;
  userId: string;
  scopeType: string;
  scopeId: string | null;
  scopeName: string | null;
  createdAt: string;
}
